import { useNotify, useRefresh, useUpdate } from "react-admin";
import { ResourceName, type StoplossClaim } from "@/types";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";
import { InlineNoteCell } from "@/components/common/InlineNoteCell";

interface ClaimNoteCellProps {
  claim: StoplossClaim;
  /** Whether the user may edit (stoploss-claims:edit + not read-only partner). */
  canEdit: boolean;
}

/**
 * Editable cell for a stop-loss claim's own `notes` field, shown on the Claims
 * Detail table. Mirrors the claim detail page's notes editing via
 * useUpdate(STOPLOSS_CLAIMS). Distinct from the claimant policy note.
 */
export const ClaimNoteCell = ({ claim, canEdit }: ClaimNoteCellProps) => {
  const [update] = useUpdate();
  const notify = useNotify();
  const notifyError = useRichApiErrorNotify();
  const refresh = useRefresh();

  const onSave = async (value: string): Promise<boolean> => {
    try {
      await update(
        ResourceName.STOPLOSS_CLAIMS,
        { id: claim.id, data: { notes: value }, previousData: claim },
        { returnPromise: true }
      );
      notify("Note saved", { type: "success" });
      // ClaimsDetailTable receives claims as a prop, so the cell's `note` won't
      // reflect the save until the parent re-fetches. Refresh to surface it
      // (matches useUpsertClaimantPolicyNote).
      refresh();
      return true;
    } catch (error) {
      notifyError(error);
      return false;
    }
  };

  return <InlineNoteCell note={claim.notes} canEdit={canEdit} onSave={onSave} />;
};
